/**
 * Admin Dashboard Analytics
 *   1. init()       – called once after institution fetch + user login
 *   2. setUser()    – called when user is known
 *   3. trackEvent() – called to record any analytics event
 * Uses the shared @ublend-npm/aula-analytics package
 */
import Analytics from '@ublend-npm/aula-analytics';
import parser from 'ua-parser-js';
import { getStore } from '../redux/store';
import { getInstitutionShortName } from '../selectors/institution';
import { getCurrentUser } from '../selectors/user';
import { buildInstitutionURL } from './institutionUrl';

// ---------------------------------------------------------------------------
// Platform & client info
// ---------------------------------------------------------------------------
const PLATFORM = 'admin';

const client = parser();

const clientInfo = {
  Browser: client.browser.name,
  'Browser Version': client.browser.version,
  'Operating System': client.os.name,
  'Operating System Version': client.os.version,
  'Screen Width': typeof screen !== 'undefined' ? screen.width : undefined,
  'Screen Height': typeof screen !== 'undefined' ? screen.height : undefined,
  'Current URL': typeof document !== 'undefined' ? document.URL : undefined,
  Referrer: typeof document !== 'undefined' ? document.referrer : undefined,
  application: 'admin',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the analytics endpoint*/
const getAnalyticsEndpoint = (): string =>
  buildInstitutionURL({ subdomain: 'apiv2', path: 'analytics' });

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialise the analytics library.
 * Call once, after `fetchInstitution()` and `loginWithSecureAuthCookie()`.
 */
export const initAnalytics = async (): Promise<void> => {
  const store = getStore();
  if (!store) return;

  const state = store.getState();
  const institution = getInstitutionShortName(state);
  const user = getCurrentUser(state);

  await Analytics.init(
    institution || '',
    user ? { userId: user.id } : {},
    PLATFORM,
    {}, // no classroom context in admin
    { ...clientInfo },
  );

  Analytics.setEndpoint(getAnalyticsEndpoint());

  if (user) {
    Analytics.setUser({ userId: user.id });
  }
};

/**
 * Update the user identity (call if user changes, rare in admin).
 */
export const setAnalyticsUser = (userId: string): void => {
  Analytics.setUser({ userId });
};

/**
 * Track an analytics event.
 *
 * Usage:
 *   import { analyticsTrackEvent } from '../utils/analytics';
 *   analyticsTrackEvent('ADMIN_ROLE_ADD_SUBMIT', { roleName, permissionCount });
 */
export const analyticsTrackEvent = (
  event: string,
  properties?: Record<string, unknown>,
): void => {
  try {
    Analytics.track({
      event,
      ...(properties || {}),
    });
  } catch {
    // Analytics not yet initialised – swallow silently.
    // In dev, the console.info fallback in trackAdminEvent still logs.
  }
};

export default Analytics;
